import { Node, mergeAttributes, ReactNodeViewRenderer, ReactNodeViewProps } from '@tiptap/react';
import { ComponentType } from 'react';

import { RiskAssessmentNodeComponent } from './risk-assessment-node-component';
import { DEFAULT_COLUMNS, RISK_ASSESSMENT_DEFAULTS } from './constants';
import type { Column, Row } from './types';

export interface RiskAssessmentNodeOptions {
  defaultColumns: Column[];
  defaultLikelihoodLabels: string[];
  defaultSeverityLabels: string[];
  editable: boolean;
}

declare module '@tiptap/react' {
  interface Commands<ReturnType> {
    riskAssessment: {
      setRiskAssessmentNode: (options?: Partial<RiskAssessmentNodeOptions>) => ReturnType;
    };
  }
}

export const RiskAssessmentNode = Node.create<RiskAssessmentNodeOptions>({
  name: 'riskAssessment',

  group: 'block',

  draggable: false,

  selectable: true,

  atom: true,

  addOptions() {
    return {
      defaultColumns: DEFAULT_COLUMNS,
      defaultLikelihoodLabels: [...RISK_ASSESSMENT_DEFAULTS.LIKELIHOOD_LABELS],
      defaultSeverityLabels: [...RISK_ASSESSMENT_DEFAULTS.SEVERITY_LABELS],
      editable: true,
    };
  },

  addAttributes() {
    return {
      columns: {
        default: this.options.defaultColumns,
      },
      rows: {
        default: [] as Row[],
      },
      likelihoodLabels: {
        default: this.options.defaultLikelihoodLabels,
      },
      severityLabels: {
        default: this.options.defaultSeverityLabels,
      },
      // Authoring flag to mark an individual node as static. Effective editability is
      // this AND editor.isEditable (see the node component), so a read-only viewer never
      // gets editable controls regardless of what this attribute was saved as.
      editable: {
        default: this.options.editable,
      },
    };
  },

  // Round-tripping a Risk Assessment node through raw HTML drops all attributes (columns,
  // rows, labels). Persistence happens via Tiptap JSON / Yjs collab only — same shape as
  // risk-assessments-node. Don't paste a serialized HTML doc back in expecting data.
  parseHTML() {
    return [{ tag: 'div[data-type="risk-assessment"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes({ 'data-type': 'risk-assessment' }, HTMLAttributes)];
  },

  addNodeView() {
    return ReactNodeViewRenderer(RiskAssessmentNodeComponent as unknown as ComponentType<ReactNodeViewProps>);
  },

  addCommands() {
    return {
      setRiskAssessmentNode:
        (options = {}) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: {
              columns: options.defaultColumns ?? this.options.defaultColumns,
              rows: [],
              likelihoodLabels: options.defaultLikelihoodLabels ?? this.options.defaultLikelihoodLabels,
              severityLabels: options.defaultSeverityLabels ?? this.options.defaultSeverityLabels,
              editable: options.editable !== undefined ? options.editable : this.options.editable,
            },
          }),
    };
  },
});
